import dayjs from 'dayjs'

import {
  VoENextActionOwner,
  VoENextActionOwnerLabels,
  VoENextActionReason,
  VoENextActionReasonLabels,
} from 'src/graphql/types/verificationOfEmployments'

export interface NextActionFields {
  nextActionOwner?: VoENextActionOwner | string | null
  nextActionReason?: VoENextActionReason | string | null
  nextActionAt?: string | Date | null
}

export interface NextActionText {
  owner: VoENextActionOwner
  /** Null while automation holds it. */
  reason: VoENextActionReason | null
  /** "Scheduled" or "Manual". */
  label: string
  /** Beside the tag: the reason, or the next automated step's date. */
  detail: string | null
  /** One sentence for the tooltip and the drawer line. */
  sentence: string
}

const asOwner = (value: unknown): VoENextActionOwner | null =>
  value === VoENextActionOwner.AUTOMATED || value === VoENextActionOwner.MANUAL
    ? value
    : null

const asReason = (value: unknown): VoENextActionReason | null =>
  typeof value === 'string' && value in VoENextActionReasonLabels
    ? (value as VoENextActionReason)
    : null

/**
 * What to show for a VoE's next action, or null when there is nothing to
 * show: the VoE is closed, or a person holds it with nothing automation ever
 * did or declined to do (a company with automation off, a first manual
 * touch still pending).
 */
export const describeNextAction = (
  voe: NextActionFields,
  now: Date = new Date()
): NextActionText | null => {
  const owner = asOwner(voe.nextActionOwner)
  if (!owner) return null
  const reason = asReason(voe.nextActionReason)
  const at = voe.nextActionAt ? dayjs(voe.nextActionAt) : null
  const shortDate = (date: dayjs.Dayjs) =>
    date.format(date.year() === dayjs(now).year() ? 'MMM D' : 'MMM D, YYYY')

  if (owner === VoENextActionOwner.AUTOMATED) {
    const label = VoENextActionOwnerLabels[owner].label
    if (!at) {
      return {
        owner,
        reason: null,
        label,
        detail: null,
        sentence: 'Automation has the next step.',
      }
    }
    const detail = shortDate(at)
    const sentence = at.isAfter(now)
      ? `Automation's next step is scheduled for ${at.format(
          'ddd, MMM D [at] h:mm A'
        )}.`
      : `Automation's next step was due ${at.format(
          'ddd, MMM D [at] h:mm A'
        )} and is running late.`
    return { owner, reason: null, label, detail, sentence }
  }

  if (!reason) return null
  const { label: reasonLabel, tooltip } = VoENextActionReasonLabels[reason]
  // When the next step last changed: a hand-back, a response, a grace period
  // running out, or the VoE's creation when automation never could start
  const updated = at ? ` Last updated ${shortDate(at)}.` : ''
  return {
    owner,
    reason,
    label: VoENextActionOwnerLabels[owner].label,
    detail: reasonLabel,
    sentence: `${tooltip}.${updated}`,
  }
}
